/**
 * @license CKCode
 * Copyright 2023 CourseKata
 * SPDX-License-Identifier: GPL-3.0-or-later
 */
import { __decorate } from "tslib";
import { MathJaxTypesetter } from "@jupyterlab/mathjax-extension";
import { MimeModel, standardRendererFactories } from "@jupyterlab/rendermime";
import { state } from "@lit/reactive-element/decorators/state.js";
import { LitElement, html, nothing } from "lit";
import { classMap } from "lit-html/directives/class-map.js";
import Plotly from "plotly.js-dist-min";
import { NamedLogger } from "../../core/logging.js";
import { UnsupportedOutputError } from "../../errors.js";
import { renderMarkdown, sanitizer } from "../../utils.js";
const log = new NamedLogger("ckcode");
export class JupyterOutputArea extends LitElement {
    constructor() {
        super(...arguments);
        this.rendererFactories = standardRendererFactories;
        this.sanitizer = sanitizer;
        this.latexTypesetter = new MathJaxTypesetter();
        this.markdownParser = new MarkdownParser();
        this._outputs = [];
    }
    get outputs() {
        return [...this._outputs];
    }
    addOutput(output) {
        this._outputs = this._mergeStreamOutputs([...this._outputs, output]);
    }
    clear() {
        this._outputs = [];
    }
    render() {
        const rendered = this._outputs.map((output) => {
            const displayData = this._convertOutputToDisplay(output);
            if (!displayData)
                return nothing;
            if (displayData.output_type === "display_data" &&
                displayData.data["application/vnd.plotly.v1+json"]) {
                return this._renderPlotly(displayData);
            }
            // find a renderer, preferring higher priority renderers and mime-types
            let preferredMimeType;
            const rendererFactory = this.rendererFactories.find((factory) => {
                const found = factory.mimeTypes.find((mimeType) => mimeType in displayData.data);
                preferredMimeType = found;
                return found;
            });
            if (!rendererFactory || !preferredMimeType)
                return nothing;
            // render the output
            const renderer = rendererFactory.createRenderer({
                mimeType: preferredMimeType,
                sanitizer: this.sanitizer,
                latexTypesetter: this.latexTypesetter,
                markdownParser: this.markdownParser,
                linkHandler: this.linkHander ?? null,
                resolver: this.resolver ?? null,
            });
            renderer.renderModel(new MimeModel({ data: displayData.data, metadata: displayData.metadata, trusted: true }));
            const isError = output.output_type === "error";
            const isStream = preferredMimeType.includes("stderr") || preferredMimeType.includes("stdout");
            const classes = {
                "output-item": true,
                "output-item--error": isError,
                "output-item--info": isStream,
            };
            return html `<div class="output-item-wrapper">
        <div class=${classMap(classes)}>${renderer.node}</div>
      </div>`;
        });
        return html `<main tabindex="0">${rendered}</main>`;
    }
    _mergeStreamOutputs(outputs) {
        return outputs.reduce((merged, output) => {
            if (merged.length === 0) {
                merged.push(output);
                return merged;
            }
            const last = merged[merged.length - 1];
            if (output.output_type !== "stream" ||
                last.output_type !== "stream" ||
                output.name !== last.name) {
                merged.push(output);
                return merged;
            }
            merged[merged.length - 1].text = this._joinMultilineStrings(last.text, output.text);
            return merged;
        }, []);
    }
    _trimNewlines(str) {
        return str.replace(/^\n+|\n+$/g, "");
    }
    _joinMultilineStrings(...multilineStrs) {
        return multilineStrs.map((multilineStr) => this._joinMultilineString(multilineStr)).join("\n");
    }
    _joinMultilineString(multilineStr) {
        return typeof multilineStr === "string"
            ? this._trimNewlines(multilineStr)
            : multilineStr.map((strs) => this._trimNewlines(strs)).join("\n");
    }
    _convertOutputToDisplay(output) {
        if (output.output_type === "display_data") {
            if (output.data["text/plain"]) {
                output.data["text/plain"] = this._trimNewlines(output.data["text/plain"]);
            }
            return output;
        }
        if (output.output_type === "stream") {
            return {
                output_type: "display_data",
                data: { [`application/vnd.jupyter.${output.name}`]: output.text },
                metadata: {},
            };
        }
        if (output.output_type === "error") {
            return {
                output_type: "display_data",
                data: { "application/vnd.jupyter.stderr": output.ename + ": " + output.evalue },
                metadata: {},
            };
        }
        if (output.output_type === "execute_result") {
            return {
                output_type: "display_data",
                data: output.data,
                metadata: output.metadata,
            };
        }
        log.warn(new UnsupportedOutputError(JSON.stringify(output)));
    }
    _renderPlotly(displayData) {
        const plotlyData = displayData.data["application/vnd.plotly.v1+json"];
        const element = document.createElement("div");
        element.className = "output-item";
        Plotly.newPlot(element, plotlyData.data, plotlyData.layout);
        return html `<div class="output-item-wrapper">
      <div class="output-item">${element}</div>
    </div>`;
    }
}
__decorate([
    state()
], JupyterOutputArea.prototype, "_outputs", void 0);
/** Wrapper class implementing the {@linkcode IRenderMime.IMarkdownParser} with marked. */
class MarkdownParser {
    async render(source) {
        return renderMarkdown(source);
    }
}
//# sourceMappingURL=jupyter-output-area.js.map